import { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase/config';
import { doc, getDoc, setDoc, getDocs, collection, query, where } from 'firebase/firestore';

const todayKey = () => new Date().toISOString().slice(0, 10);

export default function useTodaysPlan(userId, { weakTopics, getDueCount, subjects }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const docId = userId ? `${userId}_${todayKey()}` : null;
  const dueCount = getDueCount ? getDueCount() : 0;
  const signature = `${subjects?.length || 0}:${weakTopics?.length || 0}:${dueCount}`;

  const buildItems = useCallback((assignmentItems = []) => {
    const built = [...assignmentItems];
    if (weakTopics?.length) {
      const primary = weakTopics[0];
      built.push({
        id: 'practice-' + primary.key,
        label: `Practice questions on ${primary.topicKey}`,
        sub: primary.subjectName,
        link: `/lesson/${primary.subjectId}/${encodeURIComponent(primary.topicKey)}`,
        done: false,
      });
    }
    if (dueCount > 0) {
      built.push({ id: 'review-due', label: `Review ${dueCount} due item${dueCount === 1 ? '' : 's'}`, sub: 'Spaced repetition queue', link: '/review', done: false });
    }
    if (weakTopics?.length > 1) {
      const second = weakTopics[1];
      built.push({
        id: 'lesson-' + second.key,
        label: `Watch the lesson on ${second.topicKey}`,
        sub: second.subjectName,
        link: `/lesson/${second.subjectId}/${encodeURIComponent(second.topicKey)}`,
        done: false,
      });
    }
    if (!built.length) {
      built.push(subjects?.length
        ? { id: 'rush', label: 'Run a Question Rush session to stay sharp', sub: 'No urgent gaps right now', link: '/rush', done: false }
        : { id: 'add-subject', label: 'Add your first subject to get started', sub: '', link: '/dashboard', done: false });
    }
    return built.slice(0, 5);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weakTopics, dueCount, subjects]);

  useEffect(() => {
    if (!docId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Fetch assignments due today or overdue
        let assignmentItems = [];
        if (userId) {
          try {
            const aSnap = await getDocs(query(
              collection(db, 'assignments'),
              where('assignedTo', 'array-contains', userId)
            ));
            const today = new Date();
            assignmentItems = aSnap.docs
              .map(d => ({ id: d.id, ...d.data() }))
              .filter(a => new Date(a.dueDate) >= new Date(today.toDateString()) && !(a.completedBy || []).includes(userId))
              .slice(0, 2)
              .map(a => ({
                id: 'assignment-' + a.id,
                label: `📋 ${a.title}`,
                sub: `Assignment${a.subject ? ` · ${a.subject}` : ''} · due ${new Date(a.dueDate).toLocaleDateString('en-GB')}`,
                link: '/assignments',
                done: false,
                isAssignment: true,
              }));
          } catch (_) {}
        }

        const ref = doc(db, 'dailyPlans', docId);
        const snap = await getDoc(ref);
        const anyDone = snap.exists() && (snap.data().items || []).some(i => i.done);
        if (snap.exists() && (anyDone || snap.data().signature === signature) && !cancelled) {
          const existing = snap.data().items || [];
          const withAssignments = [
            ...assignmentItems.filter(a => !existing.some(e => e.id === a.id)),
            ...existing,
          ];
          setItems(withAssignments);
        } else {
          const fresh = buildItems(assignmentItems);
          if (!cancelled) {
            setItems(fresh);
            await setDoc(ref, { userId, date: todayKey(), items: fresh, signature });
          }
        }
      } catch (err) {  }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId, signature]);

  const toggleItem = async (id) => {
    const updated = items.map(i => i.id === id ? { ...i, done: !i.done } : i);
    setItems(updated);
    try { await setDoc(doc(db, 'dailyPlans', docId), { userId, date: todayKey(), items: updated, signature }); }
    catch (err) {  }
  };

  return { items, loading, toggleItem };
}